/**
 * MAKING THE CLIENT. The household, the person to ring and the home, in one batch, with the
 * enquiry marked booked in the same write.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  COUNTIES,
  PREFERRED_CHANNELS,
  humanise,
  type County,
  type PreferredChannel,
} from '@fk/shared';
import { ApiError, convertEnquiry, type EnquiryRow } from '../../api.ts';
import { Button, SelectField, TextField } from '../../components/Field.tsx';
import { ErrorNotice } from '../../components/Notice.tsx';
import { E164, formatPhone, orNull, toE164, wholeNumber } from '../../format.ts';
import { EMAIL } from './rows.ts';

/** The one ZIP in the service area that is not Pinellas. */
const PASCO_ZIP = '34655';

interface ConvertDraft {
  displayName: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  contactPreferred: PreferredChannel | '';
  addressLine1: string;
  addressLine2: string;
  town: string;
  postalCode: string;
  county: County;
  bedrooms: string;
  bathrooms: string;
}

/** Prefilled from what the enquiry already knows, and every box can be changed before saving. */
function draftFrom(row: EnquiryRow): ConvertDraft {
  return {
    displayName: row.name,
    contactName: row.name,
    contactPhone: formatPhone(row.phoneE164) ?? '',
    contactEmail: row.email ?? '',
    contactPreferred: '',
    addressLine1: '',
    addressLine2: '',
    town: row.town ?? '',
    postalCode: row.zip ?? '',
    county: row.zip?.trim() === PASCO_ZIP ? 'pasco' : 'pinellas',
    bedrooms: '',
    bathrooms: '',
  };
}

/**
 * Everything wrong with the form, in the order the fields appear, in sentences somebody can act
 * on. The server checks all of this again and is the authority; this saves the round trip.
 */
function problemsWith(draft: ConvertDraft): string[] {
  const problems: string[] = [];
  if (!draft.displayName.trim()) problems.push('The household needs a name, like "the Harrisons".');
  if (!draft.contactName.trim()) problems.push('The person to ring needs a name.');
  if (draft.contactPhone.trim() && !E164.test(toE164(draft.contactPhone) ?? '')) {
    problems.push(
      'That phone number does not look right. Ten digits is enough, like 727 337 7775.',
    );
  }
  if (draft.contactEmail.trim() && !EMAIL.test(draft.contactEmail.trim())) {
    problems.push('That email address does not look right.');
  }
  if (!draft.addressLine1.trim()) problems.push('The home needs a street address.');
  if (!draft.town.trim()) problems.push('The home needs a town.');
  if (draft.postalCode.trim().length < 5) problems.push('The home needs a five-digit ZIP.');
  for (const [label, typed] of [
    ['Beds', draft.bedrooms],
    ['Baths', draft.bathrooms],
  ] as const) {
    if (wholeNumber(typed) === undefined) problems.push(`${label} has to be a whole number.`);
  }
  return problems;
}

export function MakeClientForm({ idPrefix, row }: { idPrefix: string; row: EnquiryRow }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<ConvertDraft>(() => draftFrom(row));
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);
  const field = (name: string) => `${idPrefix}-convert-${name}-${row.id}`;

  const set = <K extends keyof ConvertDraft>(name: K, value: ConvertDraft[K]) =>
    setDraft((previous) => ({ ...previous, [name]: value }));

  const zipDisagrees =
    (draft.postalCode.trim() === PASCO_ZIP && draft.county !== 'pasco') ||
    (draft.postalCode.trim().length === 5 &&
      draft.postalCode.trim() !== PASCO_ZIP &&
      draft.county === 'pasco');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    const found = problemsWith(draft);
    setProblems(found);
    if (found.length > 0) return;

    setSaving(true);
    try {
      const made = await convertEnquiry(row.id, {
        displayName: draft.displayName.trim(),
        contact: {
          displayName: draft.contactName.trim(),
          phoneE164: draft.contactPhone.trim() ? toE164(draft.contactPhone) : null,
          email: orNull(draft.contactEmail),
          preferredChannel: draft.contactPreferred ? draft.contactPreferred : null,
        },
        site: {
          addressLine1: draft.addressLine1.trim(),
          addressLine2: orNull(draft.addressLine2),
          town: draft.town.trim(),
          postalCode: draft.postalCode.trim(),
          county: draft.county,
          bedrooms: wholeNumber(draft.bedrooms) ?? null,
          bathrooms: wholeNumber(draft.bathrooms) ?? null,
        },
      });
      // A duplicate answer names the client that already exists, and that is still where to go.
      navigate(`/clients/${made.clientId}`);
    } catch (thrown) {
      setFailure(
        thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'That client was not made.'),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="mt-3">
      <p className="mb-3 text-sm text-navy/70">
        The household, the person to ring and the home, in one go. Saving marks this enquiry booked
        and opens the new client.
      </p>

      {failure ? (
        <div className="mb-4">
          <ErrorNotice title="Nothing was saved" message={failure.message} />
        </div>
      ) : null}

      {problems.length > 0 ? (
        <div
          className="mb-4 rounded-xl border border-coral/50 border-l-4 border-l-coral bg-parchment p-4"
          role="alert"
        >
          <h3 className="font-serif text-lg">Before this can be saved</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <TextField
        id={field('household')}
        label="Household"
        required
        value={draft.displayName}
        onChange={(value) => set('displayName', value)}
        hint='As it is spoken about, "the Harrisons", not a legal name.'
        autoComplete="off"
      />

      <h4 className="mt-4 mb-2 font-serif text-base">The person to ring</h4>
      <TextField
        id={field('contact')}
        label="Name"
        required
        value={draft.contactName}
        onChange={(value) => set('contactName', value)}
        autoComplete="off"
      />
      <TextField
        id={field('phone')}
        label="Phone"
        type="tel"
        inputMode="tel"
        value={draft.contactPhone}
        onChange={(value) => set('contactPhone', value)}
        hint="Left blank, the number on the enquiry is used. Without one they cannot be told when a visit moves."
        autoComplete="off"
      />
      <TextField
        id={field('email')}
        label="Email"
        type="email"
        inputMode="email"
        value={draft.contactEmail}
        onChange={(value) => set('contactEmail', value)}
        autoComplete="off"
      />
      <SelectField
        id={field('preferred')}
        label="How they want to hear from us"
        value={draft.contactPreferred}
        onChange={(value) => set('contactPreferred', value)}
        options={PREFERRED_CHANNELS}
        toLabel={humanise}
        placeholder="Nobody has asked yet"
      />

      <h4 className="mt-4 mb-2 font-serif text-base">The home</h4>
      <TextField
        id={field('address1')}
        label="Street address"
        required
        value={draft.addressLine1}
        onChange={(value) => set('addressLine1', value)}
        autoComplete="off"
      />
      <TextField
        id={field('address2')}
        label="Unit, apartment or villa number"
        value={draft.addressLine2}
        onChange={(value) => set('addressLine2', value)}
        autoComplete="off"
      />
      <div className="grid grid-cols-2 gap-3">
        <TextField
          id={field('town')}
          label="Town"
          required
          value={draft.town}
          onChange={(value) => set('town', value)}
          autoComplete="off"
        />
        <TextField
          id={field('zip')}
          label="ZIP"
          required
          inputMode="numeric"
          value={draft.postalCode}
          onChange={(value) => set('postalCode', value)}
          autoComplete="off"
        />
      </div>
      <SelectField
        id={field('county')}
        label="County"
        required
        value={draft.county}
        onChange={(value) => set('county', value)}
        options={COUNTIES}
        toLabel={humanise}
        hint="34655 is Pasco. Every other ZIP in the service area is Pinellas."
      />
      {zipDisagrees ? (
        <p className="mb-4 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment px-3 py-2 text-sm">
          That ZIP and that county do not agree. 34655 is Pasco, and the rest is Pinellas.
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <TextField
          id={field('beds')}
          label="Beds"
          inputMode="numeric"
          value={draft.bedrooms}
          onChange={(value) => set('bedrooms', value)}
        />
        <TextField
          id={field('baths')}
          label="Baths"
          inputMode="numeric"
          value={draft.bathrooms}
          onChange={(value) => set('bathrooms', value)}
        />
      </div>
      <p className="mb-4 text-xs text-navy/65">
        Beds and baths are what put a home on a row of the published card. The do-not list, pets and
        the way to the door are typed on the client afterwards.
      </p>

      <Button type="submit" full disabled={saving}>
        {saving ? 'Saving' : 'Make the client'}
      </Button>

      {/*
       * THE WHOLE FORM, CARRYING THE ENQUIRY, for the household this short one is not enough for.
       *
       * Everything above makes a client in one write and asks for the least it can, which is right
       * for somebody working a list. What it cannot ask for is the do-not list, the pets, the way
       * to the door, the square feet or what she agreed to, and those are the answers somebody has
       * in front of her while the client is still on the phone. `/clients/new` asks for all of it
       * and takes `enquiryId`, so this row is claimed there rather than left standing beside the
       * household it became: that is the stranded enquiry `docs/handover.md` section 3 item 6
       * describes, and it had no path through the interface at all.
       */}
      <p className="mt-3 text-xs text-navy/65">
        <Link
          to={`/clients/new?enquiry=${encodeURIComponent(row.id)}`}
          className="font-medium text-navy underline"
        >
          Type them in as a household instead
        </Link>
        , on the whole form, which also asks for the do-not list, the pets, the way to the door and
        what they agreed to. This enquiry is claimed there just the same.
      </p>
    </form>
  );
}
